import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { getProject, getProjectStatus } from "apps/decooda/decoodaapi";
import { IProject } from "apps/decooda/components/projects/projectCard.types";
import { ProjectStatus } from "apps/decooda/components/projects/projectConstants";

const SCORING_STATUS_POLL_INTERVAL_MS = 3000;

export interface IAnalyticsProjectPollingResult {
  project: IProject | undefined;
  isLoading: boolean;
  isError: boolean;
  isPendingScore: boolean;
  handleProjectUpdated: () => void;
  handleScoringInitiated: () => void;
}

export const useAnalyticsProjectPolling = (
  analyticsId: string,
): IAnalyticsProjectPollingResult => {
  const queryClient = useQueryClient();
  const [isPendingScore, setIsPendingScore] = useState(false);

  const {
    data: project,
    isLoading,
    isError,
  } = useQuery<IProject, Error>(
    ["analytics-project", analyticsId],
    () => getProject({ projectId: analyticsId }),
    {
      enabled: !!analyticsId,
      refetchInterval: (data) =>
        isPendingScore || data?.status === ProjectStatus.InProgress
          ? SCORING_STATUS_POLL_INTERVAL_MS
          : false,
    },
  );

  const isInProgress = project?.status === ProjectStatus.InProgress;

  useEffect(() => {
    if (
      isInProgress ||
      project?.status === ProjectStatus.Scored ||
      project?.status === ProjectStatus.Error
    ) {
      setIsPendingScore(false);
    }
  }, [isInProgress, project?.status]);

  useQuery(
    ["analytics-project-status", analyticsId],
    () => getProjectStatus({ projectId: analyticsId }),
    {
      enabled: !!analyticsId && (isPendingScore || isInProgress),
      refetchInterval:
        isPendingScore || isInProgress
          ? SCORING_STATUS_POLL_INTERVAL_MS
          : false,
      onSuccess: (data: { status?: string }) => {
        if (data?.status && data.status !== ProjectStatus.InProgress) {
          queryClient.invalidateQueries(["analytics-project", analyticsId]);
        }
      },
    },
  );

  const handleProjectUpdated = useCallback(() => {
    queryClient.invalidateQueries(["analytics-project", analyticsId]);
  }, [analyticsId, queryClient]);

  const handleScoringInitiated = useCallback(() => {
    setIsPendingScore(true);
  }, []);

  return {
    project,
    isLoading,
    isError,
    isPendingScore,
    handleProjectUpdated,
    handleScoringInitiated,
  };
};
